// @ts-nocheck
import { useEffect, useRef } from 'react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { renderToStaticMarkup } from '@usewaypoint/email-builder';
import App from './App';
import THEME from './theme';
import {
  resetDocument,
  subscribeToDocument,
  getDocument,
} from './documents/editor/EditorContext';
import EMPTY_EMAIL_MESSAGE from './getConfiguration/sample/empty-email-message';

export type EmailDocument = Record<string, unknown>;

type Props = {
  value: EmailDocument | null | undefined;
  onChange: (doc: EmailDocument, html: string) => void;
  height?: number | string;
};

export function renderDocumentToHtml(doc: EmailDocument): string {
  // Render the exact HTML the email-builder's <Reader /> produces so the
  // in-app preview and the sent email are byte-identical. Do NOT wrap in
  // white-space: pre-wrap — that caused inconsistent spacing across mail
  // clients (Outlook/dark-mode clients rendered extra blank lines between
  // block elements). The builder already turns newlines in Text blocks
  // into proper paragraph breaks at render time.
  return renderToStaticMarkup(doc as never, { rootBlockId: 'root' });
}

export default function VisualEmailBuilder({ value, onChange, height = '70vh' }: Props) {
  const lastEmitted = useRef<unknown>(null);
  const initialized = useRef(false);
  const latestDocRef = useRef<unknown>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onChangeRef = useRef(onChange);

  // Keep latest onChange in a ref so the subscribe effect never re-runs.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Load incoming document into the store ONCE on mount. After that, ignore
  // parent updates entirely — the editor owns its state. This prevents the
  // cascade where parent setState -> re-render -> resetDocument -> selection wiped.
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const next = value && typeof value === 'object' ? value : EMPTY_EMAIL_MESSAGE;
    lastEmitted.current = next;
    latestDocRef.current = next;
    resetDocument(next as never);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to document changes and emit (debounced)
  useEffect(() => {
    const flush = () => {
      const doc = latestDocRef.current;
      if (!doc) return;
      try {
        lastEmitted.current = doc;
        const html = renderDocumentToHtml(doc as EmailDocument);
        onChangeRef.current(doc as EmailDocument, html);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('email-builder render error', e);
      }
    };

    const unsub = subscribeToDocument((doc) => {
      latestDocRef.current = doc;
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(flush, 500);
    });

    // emit initial synchronously so parent has body_html on mount
    const doc = getDocument();
    latestDocRef.current = doc;
    try {
      lastEmitted.current = doc;
      onChangeRef.current(doc as EmailDocument, renderDocumentToHtml(doc as EmailDocument));
    } catch {}

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      // flush any pending change so it isn't lost on unmount
      flush();
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={THEME}>
      <CssBaseline />
      <div
        style={{
          height,
          display: 'flex',
          flexDirection: 'column',
          background: '#f2f5f7',
          border: '1px solid hsl(var(--border))',
          borderRadius: 6,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <App />
      </div>
    </ThemeProvider>
  );
}
